<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import { computed, ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import InfoSheetButton from '@uiKit/components/InfoSheetButton.vue'
import CurrencySymbol from '@uiKit/components/CurrencySymbol.vue'
import type { CalculateFees } from '@modules/orders'
import {
  CALCULATOR_HORIZONS,
  DEFAULT_CALCULATOR_HORIZON,
  type CalculatorHorizonKey,
} from '../constants/propertyDefaults'
import { formatRate, formatWhole } from '../utils/propertyFinancialUtils'
import {
  buildCalculatorFeeRows,
  PROJECTION_SHEETS,
  projectReturns,
  type CalculatorConfig,
} from '../utils/propertyCalculatorUtils'

// INVESTMENT CALCULATOR (Figma 32590:62265): amount in with its token count,
// the total cost with its fee lines folding out beneath it, then the value
// the amount could grow to over a chosen horizon, split into appreciation /
// projected ROI / rental income.

interface Props {
  config: CalculatorConfig
  /** Amount + fees. Null while no quote or estimate is available. */
  totalCost: number | null
  /** Hides the total-cost toggle when there are no fee lines to show. */
  hasFeeBreakdown: boolean
  /**
   * The server fee quote. When given, its fee lines fold out inline under
   * "Total cost"; without it the toggle emits `view-breakdown` instead.
   */
  feeQuote?: Pick<CalculateFees, 'fees' | 'totalFeeAmount'> | null
}

const props = defineProps<Props>()

const amount = defineModel<number>('amount', { required: true })

const emit = defineEmits<{ (e: 'view-breakdown'): void }>()

const feeRows = computed(() => buildCalculatorFeeRows(props.feeQuote))
const isFeesOpen = ref(true)
const canToggleFees = computed(() => feeRows.value.length > 0 || props.hasFeeBreakdown)

const handleTotalCostToggle = () => {
  if (feeRows.value.length) isFeesOpen.value = !isFeesOpen.value
  else emit('view-breakdown')
}

const horizonKey = ref<CalculatorHorizonKey>(DEFAULT_CALCULATOR_HORIZON)
const horizon = computed(
  () =>
    CALCULATOR_HORIZONS.find((option) => option.key === horizonKey.value) ?? CALCULATOR_HORIZONS[2],
)

const tokens = computed(() =>
  props.config.tokenPrice > 0 ? Math.floor(amount.value / props.config.tokenPrice) : 0,
)
const isBelowMinimum = computed(() => amount.value > 0 && amount.value < props.config.minInvestment)
const isAboveAvailable = computed(
  () => props.config.maxInvestment > 0 && amount.value > props.config.maxInvestment,
)

const projection = computed(() =>
  projectReturns(
    amount.value,
    horizon.value.years,
    props.config.netYieldPct,
    props.config.appreciationPct,
  ),
)
const sheets = PROJECTION_SHEETS

// Digits only — letters and symbols are refused as they're typed.
const onAmountBeforeInput = (event: InputEvent) => {
  if (event.data && /\D/.test(event.data)) event.preventDefault()
}

const onAmountInput = (event: Event) => {
  const input = event.target as HTMLInputElement
  const digits = input.value.replace(/\D/g, '').replace(/^0+/, '')
  amount.value = digits ? Number(digits) : 0
  // Re-format in place: grouping commas, and no stray character left behind
  // when the amount itself didn't change (Vue wouldn't re-render then).
  input.value = digits ? formatWhole(Number(digits)) : ''
}
</script>

<template>
  <Box is="section" direction="col" gap="3" class="pd-calc">
    <Text type="body" weight="semibold" color="muted" transform="uppercase" is="h2">
      Investment Calculator
    </Text>

    <Box direction="col" class="pd-calc__card">
      <Box direction="col" gap="18px" class="pd-calc__top">
        <Box direction="col" gap="2">
          <Text is="label" for="pd-calc-amount" type="body" weight="medium">Investment Amount</Text>
          <Box align="center" gap="3" class="pd-calc__input-wrap">
            <Text type="body" weight="medium" color="var(--p-surface-600)" is="span"
              ><CurrencySymbol
            /></Text>
            <!-- Raw <input> — formatted-amount field, no kit citizen yet (§19 exception). -->
            <input
              id="pd-calc-amount"
              :value="amount ? formatWhole(amount) : ''"
              type="text"
              inputmode="numeric"
              autocomplete="off"
              placeholder="0"
              class="pd-calc__input"
              @beforeinput="onAmountBeforeInput"
              @input="onAmountInput"
            />
            <Box align="center" class="pd-calc__token-chip">
              <Text type="caption" weight="medium" color="primary" is="span"
                >{{ formatWhole(tokens) }} Tokens</Text
              >
            </Box>
          </Box>
          <Text v-if="isBelowMinimum" type="caption" color="warning" is="span">
            Minimum investment is <CurrencySymbol />{{ formatWhole(config.minInvestment) }}
          </Text>
          <Text v-else-if="isAboveAvailable" type="caption" color="warning" is="span">
            Only <CurrencySymbol />{{ formatWhole(config.maxInvestment) }} of tokens are still
            available
          </Text>
        </Box>

        <Box direction="col" gap="2">
          <Box align="center" justify="between" gap="2">
            <Text type="caption" color="var(--p-surface-600)" is="span">Total cost</Text>
            <Box
              :is="canToggleFees ? 'button' : 'div'"
              :type="canToggleFees ? 'button' : undefined"
              align="center"
              gap="1"
              class="pd-calc__total"
              :class="{ 'pd-calc__total--toggle': canToggleFees }"
              :aria-expanded="feeRows.length ? isFeesOpen : undefined"
              :aria-controls="feeRows.length ? 'pd-calc-fees' : undefined"
              :aria-haspopup="canToggleFees && !feeRows.length ? 'dialog' : undefined"
              @click="canToggleFees && handleTotalCostToggle()"
            >
              <Text type="body" weight="semibold" is="span"
                ><template v-if="totalCost != null"
                  ><CurrencySymbol />{{ formatWhole(totalCost) }}</template
                ><template v-else>-</template></Text
              >
              <Box
                v-if="canToggleFees"
                align="center"
                justify="center"
                class="pd-calc__chevron-box"
              >
                <PiIcon
                  :name="feeRows.length && isFeesOpen ? 'chevron-up' : 'chevron-down'"
                  class="pd-calc__chevron"
                />
              </Box>
            </Box>
          </Box>

          <Box
            v-if="feeRows.length && isFeesOpen"
            id="pd-calc-fees"
            direction="col"
            gap="2"
            class="pd-calc__fees"
          >
            <Box
              v-for="(row, index) in feeRows"
              :key="row.key"
              align="center"
              justify="between"
              gap="3"
              class="pd-calc__fee-row"
              :class="{ 'pd-calc__fee-row--last': index === feeRows.length - 1 }"
            >
              <Box align="center" gap="1">
                <Text
                  type="caption"
                  color="var(--p-surface-700)"
                  is="span"
                  style="line-height: 1.45; letter-spacing: -0.12px"
                  >{{ row.label }}</Text
                >
                <InfoSheetButton
                  v-if="row.info"
                  :content="row.info.sheet"
                  :label="row.info.sheet.title"
                  :size="14"
                >
                  <template #footer>
                    <Box direction="col" gap="2">
                      <Text type="caption" weight="semibold" color="default" is="span">{{
                        row.info.heading
                      }}</Text>
                      <Text type="caption" color="default" is="span"
                        ><CurrencyText :text="row.info.formula"
                      /></Text>
                    </Box>
                  </template>
                </InfoSheetButton>
              </Box>
              <Text
                type="body"
                weight="semibold"
                align="right"
                is="span"
                style="line-height: 1.45; letter-spacing: -0.14px; white-space: nowrap"
                ><CurrencyText :text="row.value"
              /></Text>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box direction="col" gap="4" class="pd-calc__results">
        <Box gap="2" role="radiogroup" aria-label="Holding period">
          <Box
            v-for="option in CALCULATOR_HORIZONS"
            :key="option.key"
            is="button"
            type="button"
            role="radio"
            align="center"
            justify="center"
            class="pd-calc__horizon"
            :class="{ 'pd-calc__horizon--active': option.key === horizonKey }"
            :aria-checked="option.key === horizonKey"
            @click="horizonKey = option.key"
          >
            <Text
              type="body-sm"
              weight="medium"
              :color="option.key === horizonKey ? 'inverse' : 'var(--p-surface-600)'"
              is="span"
            >
              {{ option.label }}
            </Text>
          </Box>
        </Box>

        <Box direction="col" align="center" gap="2">
          <Text type="body-sm" color="var(--p-surface-600)" is="span"
            >Expected value {{ horizon.caption }}</Text
          >
          <Text
            type="display"
            weight="semibold"
            is="span"
            style="font-variant-numeric: tabular-nums"
          >
            <CurrencySymbol />{{ formatWhole(projection.expectedValue) }}
          </Text>
        </Box>

        <Box gap="18px" class="pd-calc__stats">
          <Box direction="col" align="center" gap="1" class="pd-calc__stat">
            <Text type="micro" is="span" class="pd-calc__stat-label" style="font-size: 9.5px"
              >Appreciation</Text
            >
            <Box align="center" gap="1">
              <Text type="heading" weight="semibold" color="var(--p-emerald-600)" is="span">
                {{ formatRate(projection.appreciationPct) }}
              </Text>
              <InfoSheetButton
                :content="sheets.appreciation"
                :label="sheets.appreciation.title"
                :size="14"
              />
            </Box>
          </Box>
          <Box direction="col" align="center" gap="1" class="pd-calc__stat">
            <Text type="micro" is="span" class="pd-calc__stat-label" style="font-size: 9.5px"
              >Estimated ROI</Text
            >
            <Box align="center" gap="1">
              <Text type="heading" weight="semibold" color="var(--p-emerald-600)" is="span">
                {{ formatRate(projection.roiPct) }}
              </Text>
              <InfoSheetButton :content="sheets.roi" :label="sheets.roi.title" :size="14" />
            </Box>
          </Box>
          <Box direction="col" align="center" gap="1" class="pd-calc__stat">
            <Text type="micro" is="span" class="pd-calc__stat-label" style="font-size: 9.5px"
              >Rental Income</Text
            >
            <Box align="center" gap="1">
              <Text type="heading" weight="semibold" color="var(--p-emerald-600)" is="span">
                {{ formatRate(projection.rentalIncomePct) }}
              </Text>
              <InfoSheetButton :content="sheets.rental" :label="sheets.rental.title" :size="14" />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.pd-calc__card {
  border: 1px solid var(--p-surface-200);
  border-radius: 14px;
  background: var(--p-surface-0);
  overflow: hidden;
}

.pd-calc__top {
  padding: 20px;
}

.pd-calc__input-wrap {
  height: 50px;
  padding: 0 16px;
  border: 1px solid var(--p-surface-200);
  border-radius: 12px;
  background: var(--p-surface-0);
  transition: border-color 0.15s ease;
}

.pd-calc__input-wrap:focus-within {
  border-color: var(--p-primary-500);
}

.pd-calc__input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 20px;
  font-weight: 600;
  color: var(--p-text-color);
}

.pd-calc__token-chip {
  flex-shrink: 0;
  padding: 4px 10px;
  border: 1px solid var(--p-primary-600);
  border-radius: 999px;
  background: var(--p-primary-50);
}

.pd-calc__total {
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
}

.pd-calc__total--toggle {
  cursor: pointer;
}

.pd-calc__total--toggle:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
  border-radius: 4px;
}

.pd-calc__chevron-box {
  width: 24px;
  height: 24px;
}

.pd-calc__chevron {
  font-size: 14px;
  color: var(--p-primary-600);
}

/* Fee lines under "Total cost" — grey panel, hairline between rows. */
.pd-calc__fees {
  padding: 12px 13px;
  border-radius: 8px;
  background: var(--p-surface-50);
}

.pd-calc__fee-row {
  padding-bottom: 6px;
  border-bottom: 1px solid var(--p-surface-200);
}

.pd-calc__fee-row--last {
  padding-bottom: 0;
  border-bottom: none;
}

.pd-calc__results {
  padding: 16px 15px;
  border-top: 1px solid var(--p-surface-200);
}

.pd-calc__horizon {
  flex: 1 1 0;
  height: 32px;
  border: 1px solid var(--p-surface-200);
  border-radius: 999px;
  background: var(--p-surface-0);
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.pd-calc__horizon--active {
  border-color: var(--p-primary-600);
  background: var(--p-primary-600);
}

.pd-calc__horizon:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.pd-calc__stats {
  padding: 14px;
  border: 1px solid var(--p-surface-200);
  border-radius: 12px;
}

.pd-calc__stat {
  flex: 1 1 0;
  min-width: 0;
}

.pd-calc__stat-label {
  letter-spacing: 0.285px;
  white-space: nowrap;
}
</style>
